import clsx from 'clsx'
import { SubBrandName } from 'lib/settings/brands/index'
import { subBrands } from 'lib/settings/brands/sub-brands'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Control } from 'react-hook-form'
import CheckboxInput from '../CheckboxInput/CheckboxInput'
import RadioInput, { RadioInputProps } from '../RadioInput/RadioInput'

export interface TripTypeInputOnChangeArgs {
  type: TripTypeInputProps['type']
  isCruise: boolean
}

export interface TripTypeInputProps {
  /**
   * The brand to use.
   */
  brand: SubBrandName
  /**
   * The form control.
   */
  control: Control
  /**
   * Optional trip type.
   */
  type?: TripTypes
  /**
   * Optional cruise flag.
   */
  isCruise?: boolean
  /**
   * Optional change event handler.
   */
  onChange?: (data: TripTypeInputOnChangeArgs) => void
  /**
   * Optional class names to apply.
   */
  className?: string
  /**
   * Optional cruise label.
   */
  cruiseLabel?: string
  /**
   * Optional children class names to apply.
   */
  childrenClassNames?: Partial<
    Record<'RadioInput' | 'CheckboxInput', string | undefined | false>
  >
  /**
   * Optional flag to hide the cruise option.
   */
  noCruiseOption?: boolean
}

export const tripTypes = {
  roundtrip: 'Round trip',
  oneway: 'One way',
  'oneway-to-camp': 'One way to camp',
  'oneway-from-camp': 'One way from camp',
  multi: 'Multi-city',
}

export type TripTypes = keyof typeof tripTypes

export default function TripTypeInput({
  brand,
  control,
  type,
  isCruise = false,
  onChange,
  className,
  cruiseLabel = 'Going on a cruise?',
  childrenClassNames,
  noCruiseOption = false,
}: TripTypeInputProps) {
  const [defaultTypeCached, setDefaultTypeCached] = useState(
    type || 'roundtrip',
  )
  const [defaultCruiseCached, setDefaultCruiseCached] = useState(isCruise)

  const [currentType, setCurrentType] =
    useState<TripTypeInputProps['type']>(type)
  const [cruise, setCruise] = useState(isCruise)
  const tripTypeOptions: Array<{ value: TripTypes; label: string }> = useMemo(
    () =>
      (subBrands[brand].tripTypes as TripTypes[])?.map((tripTypeKey) => ({
        value: tripTypeKey,
        label: tripTypes[tripTypeKey],
      })),
    [brand],
  )

  const onTypeChange = useCallback(
    (type: TripTypeInputOnChangeArgs['type']) => {
      if (type) {
        setCurrentType(type)
        onChange?.({ type, isCruise: cruise })
      }
    },
    [onChange, cruise],
  )

  const onCruiseChange = useCallback(
    (isCruise: TripTypeInputOnChangeArgs['isCruise']) => {
      setCruise(isCruise)
      onChange?.({ isCruise, type: currentType })
    },
    [onChange, currentType],
  )

  useEffect(() => {
    if (type && type !== defaultTypeCached) {
      setDefaultTypeCached(type)
      onTypeChange(type)
    }
  }, [defaultTypeCached, type, onTypeChange])

  useEffect(() => {
    if (isCruise !== defaultCruiseCached) {
      setDefaultCruiseCached(isCruise)
      onCruiseChange(isCruise)
    }
  }, [defaultCruiseCached, isCruise, onCruiseChange])

  return (
    <div
      className={clsx(
        'flex justify-between flex-col w-full gap-y-6 md:flex-row md:items-center',
        className,
      )}
    >
      {/* Type */}
      <RadioInput
        name="type"
        control={control}
        options={tripTypeOptions}
        defaultValue={defaultTypeCached}
        labelClassName="type-body-8"
        onChange={onTypeChange as RadioInputProps['onChange']}
        className={clsx(childrenClassNames?.RadioInput)}
        displayOptionsInline
      />
      {/* Is cruise */}
      {!noCruiseOption ? (
        <CheckboxInput
          control={control}
          name="cruise"
          label={cruiseLabel}
          className={clsx('type-body-8', childrenClassNames?.CheckboxInput)}
          checked={cruise}
          onChange={onCruiseChange}
        />
      ) : null}
    </div>
  )
}
